import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import type { Crumb } from '@/lib/breadcrumbs';

/**
 * Breadcrumbs — semantic trail for nested pages.
 *
 * <nav aria-label="Breadcrumb"> with an ordered list; every level except the
 * current page is a link, the current page carries aria-current="page". Labels
 * truncate so long values (loan numbers, finding titles) never overflow or wrap
 * on small screens. Renders nothing for an empty trail.
 */
export function Breadcrumbs({ items }: { items: Crumb[] }) {
  if (items.length === 0) return null;

  return (
    <nav aria-label="Breadcrumb">
      <ol className="flex items-center gap-1 text-caption text-text-tertiary min-w-0">
        {items.map((c, i) => {
          const isLast = i === items.length - 1;
          return (
            <li key={`${c.label}-${i}`} className="flex items-center gap-1 min-w-0">
              {i > 0 && (
                <ChevronRight className="w-3 h-3 shrink-0 text-text-muted" aria-hidden />
              )}
              {c.to && !isLast ? (
                <Link
                  to={c.to}
                  className="truncate max-w-[10rem] hover:text-brand-blue hover:underline rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue/40"
                >
                  {c.label}
                </Link>
              ) : (
                <span
                  aria-current="page"
                  className="truncate max-w-[16rem] font-medium text-text-secondary"
                >
                  {c.label}
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
